import { StaticPageLayout } from '@/components/StaticPageLayout';
import { ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from '@inertiajs/react';

export default function ServicesPage() {
  const services = [
    {
      number: '01',
      title: 'Personal Statement Feedback',
      description:
        'Initial review with comments and suggestions, followed by up to 3 subsequent rounds of feedback to perfect your narrative.',
    },
    {
      number: '02',
      title: 'Work/Activities & Secondary Applications',
      description:
        'Comprehensive review of your secondary essays with up to 3 rounds of refinement.',
    },
    {
      number: '03',
      title: 'Mock Interview Preparation',
      description:
        'Video chat sessions with critical feedback for graduate school and both traditional and MMI interview formats.',
    },
    {
      number: '04',
      title: 'School Selection Assistance',
      description:
        'Personalized advice on building your school list based on your goals, experiences, and academic performance.',
    },
    {
      number: '05',
      title: 'General Application Guidance',
      description:
        'Navigate the complex application process with advice on competitiveness and strategic planning.',
    },
    // {
    //   number: '06',
    //   title: 'Professional Networking',
    //   description:
    //     'Join a network of 1,500+ mentors working to reduce structural inequities in STEM.',
    // },
  ];

  return (
    <StaticPageLayout>
      {/* Hero Section */}
      <section className="bg-white overflow-hidden">
        <div className="container mx-auto px-6 lg:px-12 max-w-7xl">
          <div className="py-24 md:py-32">
            <div className="max-w-4xl">
              <p className="text-sm uppercase tracking-widest text-primary mb-4">
                Pro Bono Services
              </p>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-light leading-tight mb-6">
                Comprehensive mentorship
                <br />
                <span className="italic">at every step,</span>
                <br />
                <span className="font-semibold">completely free</span>
              </h1>
              <p className="text-xl text-gray-700 leading-relaxed max-w-3xl">
                Medical and graduate school applications are stressful,
                complicated, and competitive. Our goal is to offer pro bono
                mentoring for individuals from diverse and underrepresented
                populations in STEM with the help of graduate students,
                residents, post-doctoral fellows, faculties, and beyond.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services List - Editorial Style */}
      <section className="py-24 bg-gray-50">
        <div className="container mx-auto px-6 lg:px-12 max-w-7xl">
          <div className="mb-16">
            <h2 className="text-sm uppercase tracking-widest text-primary mb-4">
              What We Offer
            </h2>
            <p className="text-4xl md:text-5xl font-light text-gray-900">
              Support tailored to
              <span className="italic text-primary"> your journey</span>
            </p>
          </div>

          <div className="grid md:grid-cols-1 gap-12 max-w-4xl">
            {services.map((service, index) => (
              <div key={index} className="group">
                <div className="flex gap-8 items-center">
                  <div className="flex-shrink-0">
                    <span className="text-6xl font-light text-primary/20 group-hover:text-primary/30 transition-colors">
                      {service.number}
                    </span>
                  </div>
                  <div className="pt-4">
                    <h3 className="text-2xl font-medium text-gray-900 mb-4">
                      {service.title}
                    </h3>
                    <p className="text-lg text-gray-600 leading-relaxed">
                      {service.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Important Note */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6 lg:px-12 max-w-7xl">
          <div className="max-w-4xl mx-auto">
            <div className="bg-primary/5 border border-primary/10 p-12 rounded-lg">
              <p className="text-lg text-gray-700 leading-relaxed">
                <span className="font-medium text-gray-900">Disclaimer:</span>{' '}
                Project SHORT does not guarantee admission to any specific
                school or program and does not claim to possess insider
                knowledge that may give applicants an unfair advantage. Our role
                is to provide guidance, support, and resources to help
                applicants present their best possible application.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Network Section */}
      <section className="py-24 bg-gray-50">
        <div className="container mx-auto px-6 lg:px-12 max-w-7xl">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-sm uppercase tracking-widest text-primary mb-6">
                Our Network
              </h2>
              <p className="text-3xl md:text-4xl font-light leading-relaxed text-gray-800 mb-8">
                Join a community of
                <span className="font-semibold"> 1,500+ mentors </span>
                committed to
                <span className="italic text-primary">
                  {' '}
                  reducing structural inequities
                </span>{' '}
                in STEM.
              </p>
              <p className="text-lg text-gray-600">
                Our mentors come from diverse backgrounds and have successfully
                navigated the very challenges you're facing. They volunteer
                their time because they believe in creating a more equitable
                path to professional and graduate education.
              </p>
            </div>

            <div className="relative h-[400px] flex items-center justify-center">
              <svg
                className="absolute inset-0 w-full h-full"
                viewBox="0 0 400 400"
              >
                <defs>
                  <radialGradient id="nodeGradient">
                    <stop offset="0%" stopColor="#6366f1" stopOpacity="0.2" />
                    <stop
                      offset="100%"
                      stopColor="#6366f1"
                      stopOpacity="0.05"
                    />
                  </radialGradient>
                  <radialGradient id="centralGradient">
                    <stop offset="0%" stopColor="#6366f1" stopOpacity="0.3" />
                    <stop offset="100%" stopColor="#6366f1" stopOpacity="0.1" />
                  </radialGradient>
                </defs>

                {/* Connection lines with animated pulses */}
                {[...Array(8)].map((_, i) => {
                  const angle = (i * 45 * Math.PI) / 180;
                  const x = 200 + Math.cos(angle) * 150;
                  const y = 200 + Math.sin(angle) * 150;
                  return (
                    <g key={`connection-${i}`}>
                      <line
                        x1="200"
                        y1="200"
                        x2={x}
                        y2={y}
                        stroke="#6366f1"
                        strokeOpacity="0.05"
                        strokeWidth="1"
                      />
                      <circle r="3" fill="#6366f1" opacity="0">
                        <animateMotion
                          dur="4s"
                          repeatCount="indefinite"
                          path={`M200,200 L${x},${y}`}
                          begin={`${i * 0.5}s`}
                        />
                        <animate
                          attributeName="opacity"
                          values="0;0.3;0"
                          dur="4s"
                          repeatCount="indefinite"
                          begin={`${i * 0.5}s`}
                        />
                      </circle>
                    </g>
                  );
                })}

                {/* Secondary mentee connections */}
                {[...Array(16)].map((_, i) => {
                  const angle = (i * 22.5 * Math.PI) / 180;
                  const radius = 90 + (i % 2) * 30;
                  const x = 200 + Math.cos(angle) * radius;
                  const y = 200 + Math.sin(angle) * radius;
                  const nearestMentor = Math.floor(i / 2);
                  const mentorAngle = (nearestMentor * 45 * Math.PI) / 180;
                  const mentorX = 200 + Math.cos(mentorAngle) * 150;
                  const mentorY = 200 + Math.sin(mentorAngle) * 150;

                  return (
                    <line
                      key={`secondary-${i}`}
                      x1={x}
                      y1={y}
                      x2={mentorX}
                      y2={mentorY}
                      stroke="#6366f1"
                      strokeOpacity="0.02"
                      strokeWidth="1"
                      strokeDasharray="2,4"
                    />
                  );
                })}

                {/* Central hub */}
                <g>
                  <circle cx="200" cy="200" r="35" fill="url(#centralGradient)">
                    <animate
                      attributeName="r"
                      values="35;38;35"
                      dur="3s"
                      repeatCount="indefinite"
                    />
                  </circle>
                  <text
                    x="200"
                    y="205"
                    textAnchor="middle"
                    fill="white"
                    fontSize="14"
                    fontWeight="600"
                  ></text>
                </g>

                {/* Mentor nodes */}
                {[...Array(8)].map((_, i) => {
                  const angle = (i * 45 * Math.PI) / 180;
                  const x = 200 + Math.cos(angle) * 150;
                  const y = 200 + Math.sin(angle) * 150;
                  return (
                    <g key={`mentor-${i}`}>
                      <circle cx={x} cy={y} r="22" fill="url(#nodeGradient)">
                        <animate
                          attributeName="r"
                          values="22;24;22"
                          dur="4s"
                          repeatCount="indefinite"
                          begin={`${i * 0.2}s`}
                        />
                      </circle>
                      <circle
                        cx={x}
                        cy={y}
                        r="12"
                        fill="#6366f1"
                        opacity="0.1"
                      />
                    </g>
                  );
                })}

                {/* Mentee nodes */}
                {[...Array(16)].map((_, i) => {
                  const angle = (i * 22.5 * Math.PI) / 180;
                  const radius = 90 + (i % 2) * 30;
                  const x = 200 + Math.cos(angle) * radius;
                  const y = 200 + Math.sin(angle) * radius;
                  return (
                    <circle
                      key={`mentee-${i}`}
                      cx={x}
                      cy={y}
                      r="8"
                      fill="#6366f1"
                      opacity="0.1"
                      className="animate-pulse"
                      style={{
                        animationDelay: `${i * 0.1}s`,
                        animationDuration: '3s',
                      }}
                    />
                  );
                })}
              </svg>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-white">
        <div className="container mx-auto px-6 lg:px-12 max-w-7xl">
          <div className="grid md:grid-cols-2 border-t border-gray-200">
            <div className="py-20 pr-8 border-b md:border-b-0 md:border-r border-gray-200">
              <h3 className="text-sm uppercase tracking-widest text-primary mb-4">
                For Applicants
              </h3>
              <h2 className="text-3xl md:text-4xl font-light text-gray-900 mb-6">
                Apply to be a mentee
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Application cycles are open based on whether you are applying to
                a graduate or medical school.
              </p>
              <Button
                size="lg"
                className="bg-primary hover:bg-primary/90 text-white px-8 py-6 text-base font-normal"
                asChild
              >
                <Link href="/registration/new">
                  Apply as a Mentee
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
            <div className="py-20 pl-0 md:pl-8">
              <h3 className="text-sm uppercase tracking-widest text-primary mb-4">
                For Professionals
              </h3>
              <h2 className="text-3xl md:text-4xl font-light text-gray-900 mb-6">
                Apply to be a mentor
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Share your knowledge about applications to help the next
                generation of scientists and physicians.
              </p>
              <Button
                size="lg"
                variant="outline"
                className="border-primary text-primary hover:bg-primary/5 px-8 py-6 text-base font-normal"
                asChild
              >
                <Link href="/registration/new">
                  Apply as a Mentor
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </StaticPageLayout>
  );
}
